import { create } from 'zustand'
import { persist } from 'zustand/middleware'

function resolvePrices(pricedItem) {
  const price = pricedItem?.sellPrice
  const before = pricedItem?.sellPriceBeforeDiscount ?? price
  return { price: Number(price) || 0, before: Number(before) || 0 }
}

export const useCartStore = create(
  persist(
    (set, get) => ({
      items: {}, // key -> { shopId, shopName, displayGroupName, itemId, itemName, imageUrl, price, before, quantity }
      // Add one unit of a pricedItem
      addItem: (pricedItem) => {
        const shopId = pricedItem?.shop?.id
        const shopName = pricedItem?.shop?.nameAr || pricedItem?.shop?.name || ''
        const mapId = pricedItem?.id
        if (!shopId || !mapId) return
        const key = `map:${mapId}`
        const { price, before } = resolvePrices(pricedItem)
        // Use the category name as the group label when the item belongs to a unified catalog.
        // Check pricedItem.mainCategories (the item's own list) not shop.mainCategory,
        // because a shop may have a SHOP_LIST primary category while the item is also
        // manually linked to a UNIFIED_CATALOG category.
        const unifiedCat = Array.isArray(pricedItem?.mainCategories)
          ? pricedItem.mainCategories.find(c => c.displayMode === 'UNIFIED_CATALOG')
          : null
        const displayGroupName = unifiedCat?.tagAr || shopName
        set((state) => {
          const existing = state.items[key]
          const nextQty = (existing?.quantity || 0) + 1
          return {
            items: {
              ...state.items,
              [key]: existing ? { ...existing, quantity: nextQty } : {
                key,
                shopId,
                shopName,
                displayGroupName,
                itemId: null,
                mapId,
                itemName: pricedItem?.nameAr || pricedItem?.item?.nameAr || pricedItem?.item?.name || '',
                imageUrl: pricedItem?.imageUrl || pricedItem?.item?.imageUrl || '/no-image.png',
                price,
                before,
                // bulk pricing fields (if provided by API)
                bulkMinQuantity: pricedItem?.bulkMinQuantity ?? null,
                bulkSellPrice: pricedItem?.bulkSellPrice ?? null,
                quantity: 1,
              }
            }
          }
        })
      },
      // Set quantity for a given key (remove if qty <= 0)
      setQty: (key, qty) => set((state) => {
        const q = Number(qty) || 0
        const copy = { ...state.items }
        if (q <= 0) delete copy[key]
        else copy[key] = { ...copy[key], quantity: q }
        return { items: copy }
      }),
      removeItem: (key) => set((state) => {
        const copy = { ...state.items }
        delete copy[key]
        return { items: copy }
      }),
      clear: () => set({ items: {} }),
      // Derivations
      getTotals: () => {
        const items = Object.values(get().items)
        const totalCount = items.reduce((s, it) => s + (it.quantity || 0), 0)
        const totalAfter = items.reduce((s, it) => {
          const qty = Number(it.quantity) || 0
          const unit = Number(it.price) || 0
          const bulkMin = Number(it.bulkMinQuantity)
          const hasBulk = Number.isFinite(bulkMin) && bulkMin > 0 && it.bulkSellPrice != null
          if (hasBulk && qty >= bulkMin) {
            const bulkPrice = Number(it.bulkSellPrice) || 0
            const bundles = Math.floor(qty / bulkMin)
            const bulkQty = bundles * bulkMin
            const rem = qty - bulkQty
            return s + bulkPrice * bulkQty + unit * rem
          }
          return s + unit * qty
        }, 0)
        const totalBefore = items.reduce((s, it) => s + (it.before || 0) * (it.quantity || 0), 0)
        return { totalCount, totalAfter, totalBefore }
      },
      // Group items by shopId
      byShop: () => {
        const map = {}
        Object.values(get().items).forEach((it) => {
          if (!map[it.shopId]) map[it.shopId] = { shopId: it.shopId, shopName: it.shopName, items: [] }
          map[it.shopId].items.push(it)
        })
        return map
      }
    }),
    { name: 'om_cart' }
  )
)
